import { useEffect, useMemo } from "react";
import { Box, Flex, Grid, Icon, Spinner, Text } from "@storyofams/react-ui";
import { withStory } from "@storyofams/storyblok-toolkit";
import { createClient } from "@supabase/supabase-js";
import { GetStaticProps, NextPage } from "next";
import { useRouter } from "next/router";
import { useInfiniteQuery } from "react-query";
import { enhancedStaticProps, withLayout } from "~lib";
import { Filter } from "~components/common/Icons";
import { RecipeCard } from "~components/common/RecipeCard";
import { RecipesSearchBar } from "~components/recipes/RecipesSearchBar";
import Tags from "~components/recipes/TagsFilters";
import { Button, Container, Seo } from "../../components";

const Recipes: NextPage = ({ story, categories }: any) => {
  const router = useRouter();
  const perPage: number = 9;
  const supabase = createClient(
    process.env.NEXT_PUBLIC_SUPABASE_URL,
    process.env.NEXT_PUBLIC_SUPABASE_KEY
  );
  const getRecipeItems = async (page: number): Promise<any> => {
    const start = perPage * (page - 1);
    const end = start + perPage - 1;
    try {
      //@ts-ignore
      let query = supabase
        .rpc("search_recipes_new", {
          search_term: router?.query?.q || "",
        })
        .range(start, end)
        .eq("published", true)
        .order("display_order", { ascending: true })
        .order("published_at", { ascending: false });

      if (router?.query?.tags)
        String(router?.query?.tags)
          .split(",")
          .forEach((tag) => (query = query.ilike(`tags_text`, `%${tag}%`)));
      if (router?.query?.difficulty)
        query = query.eq("difficulty_text", router?.query?.difficulty);
      if (router?.query?.category)
        query = query.eq("recipe_category", router?.query?.category);

      const { data, error } = await query;
      if (error) return [];
      return data.sort((item) =>
        //@ts-ignore
        item.title_text.toLowerCase().includes(router?.query?.q) ? -1 : 1
      );
    } catch (err) {
      console.log(err);
    }
  };

  const {
    data: recipes,
    isFetchingNextPage,
    fetchNextPage,
    hasNextPage,
    isFetching,
    remove,
  } = useInfiniteQuery(
    "getRecipeItems",
    ({ pageParam = 1 }) => getRecipeItems(pageParam),
    {
      getNextPageParam: (lastPage, allPages) =>
        lastPage?.length >= perPage ? allPages.length + 1 : undefined,
    }
  );
  const recipeItems = useMemo(() => {
    if (!recipes?.pages || recipes?.pages[0] === null) return [];
    return recipes?.pages?.reduce((all, page) => {
      all.push(...page);
      return all;
    }, []);
  }, [recipes]);
  useEffect(() => {
    if (!router?.query) {
      remove();
      fetchNextPage({ pageParam: 1 });
    }
  }, [router?.query]);
  useEffect(() => {
    remove();
    fetchNextPage({ pageParam: 1 });
  }, [router.query]);
  return (
    <>
      <Seo story={story} />
      <Container
        pt={["10px", 5]}
        px={[2]}
        maxWidth={"1332px"}
        space={0}
        justifyContent="center"
        alignItems="center"
        textAlign="center"
      >
        <Text
          fontFamily={"DINPro"}
          fontWeight={700}
          fontSize={["40px", "56px"]}
          mb={[1, 2]}
        >
          {story?.content?.title}
        </Text>
        <Text
          fontWeight={400}
          fontSize={["16px", "18px"]}
          mb={[2, "40px"]}
          maxWidth={"792px"}
          color="1D1D1BCC"
          lineHeight={["22.4px", "25px"]}
        >
          {story?.content?.description}
        </Text>
        <Flex width={"100%"} flexDirection={["column", "row"]}>
          <Box
            mr={3}
            width={["100%", "307px"]}
            border={["none", "1px solid grey200"]}
            borderRadius={"7px"}
            height={"max-content"}
            p={[0, 2]}
          >
            <Box
              display={["none", "flex"]}
              mb={2}
              justifyContent={"space-between"}
              alignItems={"center"}
            >
              <Text fontWeight={700} fontSize={"24px"}>
                Filters
              </Text>
              <Icon fontSize={"20px"} icon={Filter}></Icon>
            </Box>
            <RecipesSearchBar />
            <Tags categories={categories} />
          </Box>
          <Box width={"100%"} height={"100%"}>
            {isFetching && !hasNextPage ? (
              <Spinner py={5} />
            ) : (
              <Grid
                width="100%"
                rowGap={2}
                columnGap={2}
                rowSize={[1, 1, 3]}
                mb={"30px"}
              >
                {recipeItems &&
                  recipeItems.map((recipe) => (
                    <Box key={recipe.id}>
                      <RecipeCard content={recipe} />
                    </Box>
                  ))}
              </Grid>
            )}
            {hasNextPage && (
              <Button
                onClick={() => fetchNextPage()}
                mx="auto"
                variant="primary"
                isLoading={isFetchingNextPage}
                minWidth={["100%", "320px"]}
              >
                Laad meer recepten
              </Button>
            )}
          </Box>
        </Flex>

        {!recipeItems.length && !isFetching && (
          <Text>Recepten niet gevonden</Text>
        )}
      </Container>
    </>
  );
};

export default withLayout(withStory(Recipes));

export const getStaticProps: GetStaticProps = enhancedStaticProps(
  async ({ locale, defaultLocale, params, sdk }) => {
    let story;
    let notFound;
    try {
      story = (
        await sdk.recipesItem({
          slug: "/recepten",
        })
      ).RecipesItem;
    } catch (err) {
      notFound = true;
    }
    const supabase = createClient(
      process.env.NEXT_PUBLIC_SUPABASE_URL,
      process.env.NEXT_PUBLIC_SUPABASE_KEY
    );
    let categories = null;
    try {
      let query = supabase.from("recipe_category").select("*");
      const { data, error } = await query;
      if (error) categories = [];
      categories = data;
    } catch (err) {
      categories = [];
      console.log(err);
    }
    return {
      props: { story, categories },
      notFound,
    };
  }
);
